import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Edit2, Trash2, X, Users, CheckCircle, RefreshCw, Package } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';
import '../styles/Subscriptions.css';

const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'Credit Card', 'Cheque', 'Online'];
const CURRENCIES    = ['USD', 'EUR', 'GBP', 'AED', 'INR', 'SAR', 'QAR', 'KWD'];

// Quick presets [users, months]
const PRESETS = [
  [5,1],[5,3],[5,6],[5,12],
  [10,1],[10,3],[10,6],[10,12],
  [25,6],[25,12],[50,12],
];

const emptyForm = {
  subscriptionid: null,
  max_users: '', duration_months: '',
  amount_paid: '', currency: 'USD',
  payment_reference: '', payment_mode: 'Cash',
  notes: '', isactive: 1,
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const Subscriptions = () => {
  const [subs,      setSubs]      = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode,  setEditMode]  = useState(false);
  const [form,      setForm]      = useState(emptyForm);
  const [errors,    setErrors]    = useState({});
  const [saving,    setSaving]    = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Subscriptions.php');
      if (res.data.success) {
        setSubs(res.data.data || []);
        setSummary(res.data.summary || null);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const applyPreset = (users, months) =>
    setForm(f => ({ ...f, max_users: users, duration_months: months }));

  const validate = () => {
    const e = {};
    if (!form.max_users || form.max_users < 1)             e.max_users       = 'Enter number of users (min 1)';
    if (!form.duration_months || form.duration_months < 1) e.duration_months = 'Enter duration in months (min 1)';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiClient[editMode ? 'put' : 'post']('/Subscriptions.php', form);
      if (res.data.success) {
        alert(res.data.message || (editMode ? 'Updated!' : 'Subscription created!'));
        setShowModal(false); resetForm(); fetchAll();
      } else alert('Error: ' + res.data.message);
    } catch (e) { alert(e.response?.data?.message || 'Error saving.'); }
    finally { setSaving(false); }
  };

  const handleEdit = s => {
    setForm({
      subscriptionid: s.subscriptionid, max_users: s.max_users,
      duration_months: s.duration_months, amount_paid: s.amount_paid,
      currency: s.currency, payment_reference: s.payment_reference || '',
      payment_mode: s.payment_mode || 'Cash', notes: s.notes || '', isactive: s.isactive,
    });
    setEditMode(true); setShowModal(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('Deactivate this subscription?')) return;
    try {
      const res = await apiClient.delete('/Subscriptions.php', { data: { subscriptionid: id } });
      if (res.data.success) fetchAll();
      else alert('Error: ' + res.data.message);
    } catch { alert('Error deactivating.'); }
  };

  const resetForm  = () => { setForm(emptyForm); setEditMode(false); setErrors({}); };
  const closeModal = () => { setShowModal(false); resetForm(); };

  const limitColor = () => {
    if (!summary?.has_subscription) return '#dc2626';
    const pct = summary.active_count / summary.max_users;
    return pct >= 1 ? '#dc2626' : pct >= 0.8 ? '#f59e0b' : '#16a34a';
  };

  return (
    <div className="master-container">
      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2><CreditCard size={32} style={{ display:'inline', marginRight:10, verticalAlign:'middle' }}/>Subscription Management</h2>
          <p className="subtitle">Purchase user slots · Each user's expiry is set automatically at creation time</p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn-icon btn-edit" onClick={fetchAll} style={{ padding:8 }}><RefreshCw size={18}/></button>
          <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}><Plus size={20}/> New Subscription</button>
        </div>
      </div>

      {/* Active plan summary */}
      {summary && (
        <div style={{ background:'white', borderRadius:12, border:`2px solid ${limitColor()}22`, padding:'18px 22px', marginBottom:22, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:14, boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:48, height:48, borderRadius:12, background:limitColor()+'18', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Package size={24} style={{ color:limitColor() }}/>
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:15 }}>
                {summary.has_subscription
                  ? `Active Plan: ${summary.max_users} Users · ${summary.duration_months} Month${summary.duration_months > 1 ? 's' : ''}`
                  : 'No Active Subscription'}
              </div>
              <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>
                {summary.has_subscription
                  ? `${summary.active_count} of ${summary.max_users} slots used · New users get ${summary.duration_months} month${summary.duration_months > 1 ? 's' : ''} from their creation date`
                  : 'Purchase a subscription to add users'}
              </div>
            </div>
          </div>
          {summary.has_subscription && (
            <div style={{ display:'flex', gap:24 }}>
              {[
                { label:'Max Users',   val: summary.max_users },
                { label:'Active Users',val: summary.active_count },
                { label:'Slots Left',  val: Math.max(0, summary.max_users - summary.active_count) },
                { label:'Duration',    val: summary.duration_months + 'mo' },
              ].map(({ label, val }) => (
                <div key={label} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:800 }}>{val}</div>
                  <div style={{ fontSize:10, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-state">Loading subscriptions…</div>
      ) : subs.length === 0 ? (
        <div className="empty-state">
          <CreditCard size={44} style={{ color:'#d1d5db', marginBottom:10 }}/>
          <p>No subscriptions yet.</p>
          <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>Buy First Subscription</button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Users</th>
                <th>Duration</th>
                <th>User Expiry Rule</th>
                <th>Users Linked</th>
                <th>Slots Left</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Created</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.subscriptionid}>
                  <td>
                    <span style={{ background:'#ede9fe', color:'#6d28d9', borderRadius:20, padding:'3px 10px', fontWeight:700, fontSize:12, display:'inline-flex', alignItems:'center', gap:4 }}>
                      <Users size={12}/>{s.max_users}
                    </span>
                  </td>
                  <td style={{ fontWeight:600 }}>{s.duration_months} month{s.duration_months > 1 ? 's' : ''}</td>
                  <td style={{ color:'#6b7280', fontSize:12 }}>Creation date + {s.duration_months} month{s.duration_months > 1 ? 's' : ''}</td>
                  <td style={{ textAlign:'center', fontWeight:700 }}>{s.users_linked ?? '—'}</td>
                  <td style={{ textAlign:'center' }}>
                    <span style={{ background: s.slots_left === 0 ? '#fef2f2' : '#f0fdf4', color: s.slots_left === 0 ? '#dc2626' : '#16a34a', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>
                      {s.slots_left ?? '—'}
                    </span>
                  </td>
                  <td>{s.currency} {parseFloat(s.amount_paid).toFixed(2)}</td>
                  <td>
                    <div style={{ fontSize:12 }}>{s.payment_mode}</div>
                    {s.payment_reference && <div style={{ fontSize:11, color:'#9ca3af' }}>{s.payment_reference}</div>}
                  </td>
                  <td style={{ fontSize:12, color:'#6b7280' }}>{fmtDate(s.createdat)}</td>
                  <td><span className={`status-badge ${s.isactive ? 'active' : 'inactive'}`}>{s.isactive ? 'Active' : 'Inactive'}</span></td>
                  <td className="actions-column">
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(s)}><Edit2 size={16}/></button>
                    {s.isactive && <button className="btn-icon btn-delete" onClick={() => handleDelete(s.subscriptionid)}><Trash2 size={16}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" style={{ maxWidth:560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Subscription' : 'New Subscription'}</h3>
              <button className="btn-close" onClick={closeModal}><X size={24}/></button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {/* Info callout */}
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#1d4ed8', display:'flex', gap:8, alignItems:'flex-start' }}>
                <CheckCircle size={15} style={{ flexShrink:0, marginTop:1 }}/>
                <span>User expiry is set <strong>automatically when each user is created</strong>: their account creation date + the duration months below.</span>
              </div>

              {/* Quick presets */}
              {!editMode && (
                <div style={{ marginBottom:16 }}>
                  <label style={{ display:'block', marginBottom:6, fontSize:12, fontWeight:600, color:'#374151' }}>Quick Select</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {PRESETS.map(([u, m]) => {
                      const active = form.max_users == u && form.duration_months == m;
                      return (
                        <button key={`${u}-${m}`} type="button" onClick={() => applyPreset(u, m)}
                          style={{ padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', transition:'all .15s', background: active ? 'linear-gradient(135deg,#667eea,#764ba2)' : 'white', color: active ? 'white' : '#374151', border: active ? '1.5px solid transparent' : '1.5px solid #e5e7eb' }}>
                          {u}u · {m}m
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Users <span className="required">*</span></label>
                  <input type="number" min="1" max="1000" value={form.max_users}
                    onChange={e => setForm({ ...form, max_users: e.target.value })}
                    placeholder="e.g. 10" className={errors.max_users ? 'error' : ''}/>
                  {errors.max_users && <span className="error-message">{errors.max_users}</span>}
                  <small style={{ color:'#6b7280' }}>Max active user slots</small>
                </div>
                <div className="form-group">
                  <label>Duration (Months) <span className="required">*</span></label>
                  <input type="number" min="1" max="120" value={form.duration_months}
                    onChange={e => setForm({ ...form, duration_months: e.target.value })}
                    placeholder="e.g. 6" className={errors.duration_months ? 'error' : ''}/>
                  {errors.duration_months && <span className="error-message">{errors.duration_months}</span>}
                  <small style={{ color:'#6b7280' }}>Each user expires this many months after their account is created</small>
                </div>
              </div>

              {/* Preview pill */}
              {form.max_users && form.duration_months && (
                <div style={{ background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:13, color:'#5b21b6', display:'flex', alignItems:'center', gap:8 }}>
                  <Package size={15}/>
                  <span><strong>{form.max_users} users</strong>, each active for <strong>{form.duration_months} month{form.duration_months > 1 ? 's' : ''}</strong> from their creation date</span>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Amount Paid</label>
                  <input type="number" min="0" step="0.01" value={form.amount_paid}
                    onChange={e => setForm({ ...form, amount_paid: e.target.value })} placeholder="0.00"/>
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })}>
                    {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Reference</label>
                  <input type="text" value={form.payment_reference}
                    onChange={e => setForm({ ...form, payment_reference: e.target.value })}
                    placeholder="Txn ID / Cheque no." maxLength="100"/>
                </div>
              </div>

              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label>Notes</label>
                <textarea rows="2" value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any additional notes…"/>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editMode ? 'Update' : 'Create Subscription'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;